import { useRef, useCallback, useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

interface AutoSaveOptions<T> {
  initialData: T;
  onSave: (data: T) => Promise<{ success: boolean; updated?: boolean; updatedAt?: Date }>;
  debounceMs?: number;
  toastThrottleMs?: number;
}

interface AutoSaveState {
  isDirty: boolean;
  isSaving: boolean;
  lastSavedAt: Date | null;
  syncStatus: 'synced' | 'saving' | 'error';
}

export function useAutoSave<T>({
  initialData,
  onSave,
  debounceMs = 3000,
  toastThrottleMs = 30000
}: AutoSaveOptions<T>) {
  const [data, setData] = useState<T>(initialData);
  const [state, setState] = useState<AutoSaveState>({
    isDirty: false,
    isSaving: false,
    lastSavedAt: null,
    syncStatus: 'synced'
  });

  const { toast } = useToast();
  const snapshotRef = useRef<T>(initialData);
  const debounceRef = useRef<NodeJS.Timeout>();
  const lastToastRef = useRef<number>(0);
  const savePromiseRef = useRef<Promise<void> | null>(null);

  // Update snapshot when initial data changes
  useEffect(() => {
    snapshotRef.current = initialData;
    setData(initialData);
    setState(prev => ({ ...prev, isDirty: false, syncStatus: 'synced' }));
  }, [initialData]);

  // Check if data is dirty compared to snapshot
  const isDirty = useCallback((currentData: T): boolean => {
    return JSON.stringify(currentData) !== JSON.stringify(snapshotRef.current);
  }, []);

  // Save function with debounce
  const saveDraft = useCallback(async (dataToSave: T) => {
    if (!isDirty(dataToSave)) {
      setState(prev => ({ ...prev, isDirty: false, syncStatus: 'synced' }));
      return;
    }

    setState(prev => ({ ...prev, isSaving: true, syncStatus: 'saving' }));

    try {
      const result = await onSave(dataToSave);
      
      if (result.success) {
        // Update snapshot only if save was successful
        snapshotRef.current = dataToSave;
        
        setState(prev => ({
          ...prev,
          isDirty: false,
          isSaving: false,
          lastSavedAt: result.updatedAt || new Date(),
          syncStatus: 'synced'
        }));

        // Show toast only if there was an actual update and throttle is respected
        const now = Date.now();
        const shouldShowToast = result.updated && (now - lastToastRef.current) > toastThrottleMs;
        
        if (shouldShowToast) {
          lastToastRef.current = now;
          toast({
            title: "✓ Salvo automaticamente",
            description: "Alterações foram salvas",
            duration: 2500,
          });
        }
      } else {
        setState(prev => ({ ...prev, isSaving: false, syncStatus: 'error' }));
        
        toast({
          title: "Falha ao salvar",
          description: "Erro ao salvar alterações. Tentar novamente?",
          variant: "destructive",
          duration: 5000,
        });
      }
    } catch (error) {
      setState(prev => ({ ...prev, isSaving: false, syncStatus: 'error' }));
      
      toast({
        title: "Falha ao salvar",
        description: "Erro de conexão. Tentar novamente?",
        variant: "destructive", 
        duration: 5000,
      });
    }
  }, [onSave, isDirty, toast, toastThrottleMs]);

  // Update data with auto-save
  const updateData = useCallback((updater: T | ((prev: T) => T)) => {
    setData(prevData => {
      const newData = typeof updater === 'function' ? (updater as (prev: T) => T)(prevData) : updater;
      
      // Mark as dirty if different from snapshot
      const isDataDirty = isDirty(newData);
      setState(prev => ({ ...prev, isDirty: isDataDirty }));

      // Clear existing timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Set new timeout only if data is dirty
      if (isDataDirty) {
        debounceRef.current = setTimeout(() => {
          // Prevent concurrent saves
          if (!savePromiseRef.current) {
            savePromiseRef.current = saveDraft(newData).finally(() => {
              savePromiseRef.current = null;
            });
          }
        }, debounceMs);
      }

      return newData;
    });
  }, [isDirty, saveDraft, debounceMs]);

  // Manual save (bypasses debounce)
  const saveNow = useCallback(async () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    if (!savePromiseRef.current && state.isDirty) {
      savePromiseRef.current = saveDraft(data).finally(() => {
        savePromiseRef.current = null;
      });
    }
    
    return savePromiseRef.current;
  }, [data, saveDraft, state.isDirty]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Get inline status text
  const getInlineStatus = useCallback(() => {
    if (state.isSaving) {
      return { text: "Salvando…", type: 'saving' as const };
    }
    
    if (state.syncStatus === 'error') {
      return { text: "Erro ao salvar", type: 'error' as const };
    }
    
    if (state.lastSavedAt && !state.isDirty) {
      const timeStr = state.lastSavedAt.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });
      return { text: `Tudo sincronizado • ${timeStr}`, type: 'synced' as const };
    }
    
    // Show sync status even without lastSavedAt if data is clean
    if (!state.isDirty && state.syncStatus === 'synced') {
      const now = new Date();
      const timeStr = now.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });
      return { text: `Tudo sincronizado • ${timeStr}`, type: 'synced' as const };
    }
    
    return null;
  }, [state]);

  return {
    data,
    updateData,
    saveNow,
    state,
    getInlineStatus
  };
}